import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';

const TARGET_DATE = new Date('2026-04-01T08:00:00Z').getTime();
const ADMIN_CODE = 'npt2026';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const calculateTimeLeft = (): TimeLeft => {
  const diff = Math.max(0, TARGET_DATE - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const formatLocalLaunchDate = (): string => {
  const target = new Date(TARGET_DATE);
  return target.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const pad = (n: number) => String(n).padStart(2, '0');

const FlipDigit = ({ value, label }: { value: string; label: string }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    if (value !== displayValue) {
      setFlipping(true);
      const timeout = setTimeout(() => {
        setDisplayValue(value);
        setFlipping(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [value, displayValue]);

  return (
    <div className="flex flex-col items-center gap-3 sm:gap-4">
      <div className="relative group">
        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-amber-500/20 via-slate-700/30 to-slate-700/10 opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative bg-slate-900/80 backdrop-blur-2xl rounded-2xl min-w-[68px] sm:min-w-[90px] md:min-w-[110px] lg:min-w-[130px] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] via-transparent to-black/20 pointer-events-none" />
          <div className="relative px-3 sm:px-5 md:px-6 py-4 sm:py-5 md:py-7 flex items-center justify-center">
            <span
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tabular-nums tracking-tight transition-all duration-150 ${
                flipping ? 'scale-y-0 opacity-0' : 'scale-y-100 opacity-100'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {displayValue}
            </span>
          </div>
          <div className="absolute left-2 right-2 top-1/2 h-[1px] bg-slate-800/80 pointer-events-none" />
        </div>
      </div>
      <span className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-[0.25em] font-medium">
        {label}
      </span>
    </div>
  );
};

const Countdown = ({ onBypass }: { onBypass: () => void }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [mounted, setMounted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const localLaunchDate = useMemo(() => formatLocalLaunchDate(), []);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const t = calculateTimeLeft();
      setTimeLeft(t);
      if (t.days === 0 && t.hours === 0 && t.minutes === 0 && t.seconds === 0) {
        clearInterval(timer);
        onBypass();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [onBypass]);

  useEffect(() => {
    if (showCodeInput && inputRef.current) inputRef.current.focus();
  }, [showCodeInput]);

  const handleSubmit = useCallback(() => {
    if (code.toLowerCase().trim() === ADMIN_CODE) {
      sessionStorage.setItem('countdown_bypass', 'true');
      onBypass();
    } else {
      setCodeError(true);
      setTimeout(() => setCodeError(false), 600);
      setCode('');
    }
  }, [code, onBypass]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSubmit();
      if (e.key === 'Escape') {
        setShowCodeInput(false);
        setCode('');
        setCodeError(false);
      }
    },
    [handleSubmit]
  );

  const isFinished =
    timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

  const totalSeconds = timeLeft.days * 86400 + timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds;
  const progressPercent = Math.max(0, Math.min(100, 100 - (totalSeconds / (365 * 86400)) * 100));

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 flex flex-col items-center justify-center overflow-hidden select-none">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-amber-500/[0.04] rounded-full blur-[150px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-[-15%] right-[-5%] w-[500px] h-[500px] bg-blue-500/[0.05] rounded-full blur-[130px] animate-pulse" style={{ animationDuration: '10s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-500/[0.02] rounded-full blur-[200px]" />
      </div>

      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(148, 163, 184, 0.03) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

      <div
        className={`relative z-10 flex flex-col items-center px-6 w-full max-w-4xl transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="mb-10 sm:mb-14 flex flex-col items-center">
          <img
            src="./images/Nextproptrader Logo W.png"
            alt="Nextproptrader"
            className="h-8 sm:h-10 lg:h-12 w-auto mb-8 sm:mb-10"
          />

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/[0.06] backdrop-blur-sm mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs sm:text-sm font-medium text-amber-400 uppercase tracking-[0.2em]">
              {isFinished ? 'Now Live' : 'Launching Soon'}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center leading-tight mb-4 sm:mb-5">
            {isFinished ? (
              'The Future Is Here'
            ) : (
              <>
                The Next Generation
                <br />
                <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent">
                  of Prop Trading
                </span>
              </>
            )}
          </h1>

          <p className="text-sm sm:text-base text-slate-400 text-center max-w-lg leading-relaxed font-light">
            {isFinished
              ? 'Welcome to the new era. Start your trading journey today.'
              : 'We are building something extraordinary. Be ready when the markets open.'}
          </p>
        </div>

        {!isFinished && (
          <>
            <div className="flex items-start gap-3 sm:gap-4 md:gap-6">
              <FlipDigit value={String(timeLeft.days)} label="Days" />
              <div className="flex flex-col items-center justify-center gap-2.5 pt-5 sm:pt-6 md:pt-8">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-400/50" />
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-400/50" />
              </div>
              <FlipDigit value={pad(timeLeft.hours)} label="Hours" />
              <div className="flex flex-col items-center justify-center gap-2.5 pt-5 sm:pt-6 md:pt-8">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-400/50" />
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-400/50" />
              </div>
              <FlipDigit value={pad(timeLeft.minutes)} label="Minutes" />
              <div className="flex flex-col items-center justify-center gap-2.5 pt-5 sm:pt-6 md:pt-8">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-400/50" />
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-400/50" />
              </div>
              <FlipDigit value={pad(timeLeft.seconds)} label="Seconds" />
            </div>

            <div className="w-full max-w-md mt-10 sm:mt-14">
              <div className="h-[2px] w-full bg-slate-800/80 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500/60 via-amber-400 to-amber-500/60 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </>
        )}

        <div className="mt-8 sm:mt-10 text-center">
          <p className="text-xs sm:text-sm text-slate-500 font-light">
            {localLaunchDate}
          </p>
        </div>
      </div>

      <div className="fixed bottom-4 right-4 z-50">
        {!showCodeInput ? (
          <button
            onClick={() => setShowCodeInput(true)}
            className="w-7 h-7 rounded-full flex items-center justify-center text-slate-800 hover:text-slate-500 transition-all duration-500 opacity-20 hover:opacity-80"
            aria-label="Admin access"
          >
            <Lock className="w-3 h-3" />
          </button>
        ) : (
          <div
            className={`flex items-center gap-2 bg-slate-900/95 backdrop-blur-2xl border rounded-xl p-2 shadow-2xl shadow-black/40 transition-all duration-300 ${
              codeError ? 'border-red-500/50 animate-shake' : 'border-slate-700/40'
            }`}
          >
            <div className="relative">
              <input
                ref={inputRef}
                type={showCode ? 'text' : 'password'}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Access code"
                className="w-32 sm:w-40 bg-slate-800/50 border border-slate-700/30 rounded-lg px-3 py-1.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/40 pr-8 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowCode(!showCode)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors"
              >
                {showCode ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
            </div>
            <button
              onClick={handleSubmit}
              className="w-7 h-7 rounded-lg bg-gradient-to-r from-amber-500/20 to-amber-400/10 border border-amber-500/20 flex items-center justify-center text-amber-400 hover:from-amber-500/30 hover:to-amber-400/20 transition-all duration-200"
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Countdown;
